// i18n
import { translateInComponent } from 'infras/i18n';
import translations from './i18n.json';

import styled from '@emotion/styled';
import { css } from '@emotion/react';
import TextArea from 'presentations/views/components/atoms/TextArea';
import {
  mediaMaxWithLayoutTheme,
  fontSize,
  spacing,
  mediaMax,
} from 'presentations/views/styles';
import { gray04 } from 'presentations/views/styles/color';

import Button from 'presentations/views/components/atoms/Button';
import TextField from 'presentations/views/components/atoms/TextField';
import { ErrorList } from 'domains/models';
import ExpandForm from 'presentations/views/components/molecules/ExpandForm';
import DashboardFormSubmitField from 'presentations/views/components/molecules/DashboardFormSubmitField';
import { ViewerItem } from 'presentations/api/schema/user';

interface Props {
  errors: ErrorList;
  onSubmit?: any;
  onChange?: any;
  onChangeDescription?: any;
  onBlur?: any;
  onEdit?: any;
  isLoading?: boolean;
  isDisabled?: boolean;
  viewer: ViewerItem;
  isOpen: boolean;
  onExpand?: any;
  onCancel?: any;
  name: string | null;
  owner: string | null;
  address: string | null;
  tel: string | null;
  priceDescription: string | null;
  paymentMethodDescription: string | null;
  paymentScheduleDescription: string | null;
  feeDescription: string | null;
  serviceDescription: string | null;
  refundDescription: string | null;
  environmentDescription: string | null;
}

export default function Component(props: Props) {
  const t = translateInComponent([translations]);

  const fieldSize = undefined;

  const form = (
    <form noValidate autoComplete="off">
      <Content>
        <Block>
          <TextFieldWrap
            isRequired
            name={'name'}
            value={props.name}
            width={400}
            label={t('name')}
            placeholder={t('name placeholder')}
            onChange={props.onChange}
            onBlur={props.onBlur}
            errors={props.errors['name']}
            size={fieldSize}
          />
        </Block>

        <Block>
          <TextFieldWrap
            isRequired
            name={'owner'}
            value={props.owner}
            width={400}
            label={t('owner')}
            placeholder={t('owner placeholder')}
            onChange={props.onChange}
            onBlur={props.onBlur}
            errors={props.errors['owner']}
            size={fieldSize}
          />
        </Block>

        <Block>
          <TextFieldWrap
            isRequired
            isFullWidth
            name={'address'}
            value={props.address}
            label={t('address')}
            placeholder={t('address placeholder')}
            onChange={props.onChange}
            onBlur={props.onBlur}
            errors={props.errors['address']}
            size={fieldSize}
          />
        </Block>

        <Block>
          <TextFieldWrap
            isRequired
            name={'tel'}
            value={props.tel}
            width={400}
            label={t('tel')}
            placeholder={t('tel placeholder')}
            onChange={props.onChange}
            onBlur={props.onBlur}
            errors={props.errors['tel']}
            size={fieldSize}
          />
        </Block>

        <Block>
          <TextAreaWrap
            isRequired
            isFullWidth={true}
            label={t('priceDescription')}
            name={'priceDescription'}
            value={props.priceDescription}
            onChange={props.onChange}
            errors={props.errors['priceDescription']}
          />
        </Block>

        <Block>
          <TextAreaWrap
            isRequired
            isFullWidth={true}
            label={t('paymentMethodDescription')}
            name={'paymentMethodDescription'}
            value={props.paymentMethodDescription}
            onChange={props.onChange}
            errors={props.errors['paymentMethodDescription']}
          />
        </Block>

        <Block>
          <TextAreaWrap
            isRequired
            isFullWidth={true}
            label={t('paymentScheduleDescription')}
            name={'paymentScheduleDescription'}
            value={props.paymentScheduleDescription}
            onChange={props.onChange}
            errors={props.errors['paymentScheduleDescription']}
          />
        </Block>

        <Block>
          <TextAreaWrap
            isRequired
            isFullWidth={true}
            label={t('feeDescription')}
            name={'feeDescription'}
            value={props.feeDescription}
            onChange={props.onChange}
            errors={props.errors['feeDescription']}
          />
        </Block>

        <Block>
          <TextAreaWrap
            isRequired
            isFullWidth={true}
            label={t('serviceDescription')}
            name={'serviceDescription'}
            value={props.serviceDescription}
            onChange={props.onChange}
            errors={props.errors['serviceDescription']}
          />
        </Block>

        <Block>
          <TextAreaWrap
            isRequired
            isFullWidth={true}
            label={t('refundDescription')}
            name={'refundDescription'}
            value={props.refundDescription}
            onChange={props.onChange}
            errors={props.errors['refundDescription']}
          />
        </Block>

        <Block>
          <TextAreaWrap
            isRequired
            isFullWidth={true}
            label={t('environmentDescription')}
            name={'environmentDescription'}
            value={props.environmentDescription}
            onChange={props.onChange}
            errors={props.errors['environmentDescription']}
          />
        </Block>
      </Content>
    </form>
  );

  if (props.isOpen) {
    return (
      <ExpandForm
        isOpen={true}
        label={t('title')}
        helperText={t('desc')}
        name="basic"
        onExpand={props.onExpand}
        content={form}
        submitField={
          <DashboardFormSubmitField
            isLoading={props.isLoading}
            onSubmit={props.onSubmit}
            isDisabled={props.isDisabled}
            onCancel={props.onCancel}
          />
        }
      />
    );
  } else {
    return (
      <Container>
        <HostServicePaymentContainer>
          <ActionContainer>
            <TitleContainer>
              <TitleInner>{t('title')}</TitleInner>
            </TitleContainer>
          </ActionContainer>
          <Desc>{t('desc')}</Desc>
          <ProfileContainer>
            <ProfileTitle isFirstItem={true}>{t('name')}</ProfileTitle>
            <ProfileValue>{props.name}</ProfileValue>
            <ProfileTitle>{t('owner')}</ProfileTitle>
            <ProfileValue>{props.owner}</ProfileValue>
            <ProfileTitle>{t('address')}</ProfileTitle>
            <ProfileValue>{props.address}</ProfileValue>
            <ProfileTitle>{t('tel')}</ProfileTitle>
            <ProfileValue>{props.tel}</ProfileValue>
            <ProfileTitle>{t('priceDescription')}</ProfileTitle>
            <ProfileValue>{props.priceDescription}</ProfileValue>
            <ProfileTitle>{t('paymentMethodDescription')}</ProfileTitle>
            <ProfileValue>{props.paymentMethodDescription}</ProfileValue>
            <ProfileTitle>{t('paymentScheduleDescription')}</ProfileTitle>
            <ProfileValue>{props.paymentScheduleDescription}</ProfileValue>
            <ProfileTitle>{t('feeDescription')}</ProfileTitle>
            <ProfileValue>{props.feeDescription}</ProfileValue>
            <ProfileTitle>{t('serviceDescription')}</ProfileTitle>
            <ProfileValue>{props.serviceDescription}</ProfileValue>
            <ProfileTitle>{t('refundDescription')}</ProfileTitle>
            <ProfileValue>{props.refundDescription}</ProfileValue>
            <ProfileTitle>{t('environmentDescription')}</ProfileTitle>
            <ProfileValue>{props.environmentDescription}</ProfileValue>

            <StartButton>
              <StartButtonInner
                size={'medium'}
                type="submit"
                color="primary"
                onClick={props.onEdit}
              >
                {t('Edit')}
              </StartButtonInner>
            </StartButton>
          </ProfileContainer>
        </HostServicePaymentContainer>
      </Container>
    );
  }
}

const Content = styled.div``;
const Block = styled.div`
  padding: ${spacing(32)} 0;
  :first-of-type {
    padding: 0 0 ${spacing(32)};
  }
  border-bottom: 1px solid ${gray04};
  :last-of-type {
    border-bottom: 0;
  }

  ${(props) => mediaMaxWithLayoutTheme(props, 'tablet')} {
    padding: ${spacing(12)} 0;
    :first-of-type {
      padding: 0 0 ${spacing(12)};
    }
  }
`;
const TextFieldWrap = styled(TextField)`
  ${(props) => mediaMaxWithLayoutTheme(props, 'desktop')} {
    width: 100%;
  }
`;

const TextAreaWrap = styled(TextArea)`
  ${(props) => mediaMaxWithLayoutTheme(props, 'desktop')} {
    width: 100%;
  }
`;

const Container = styled.div`
  display: flex;
  ${mediaMax.tablet} {
    flex-direction: column;
  }
  padding-bottom: ${spacing(24)};
`;
const HostServicePaymentContainer = styled.div`
  width: 100%;
  box-sizing: border-box;
  padding: ${spacing(32)} ${spacing(48)};
  ${mediaMax.tablet} {
    margin-top: ${spacing(24)};
    padding: 0 ${spacing(12)};
  }
`;
const TitleInner = styled.div`
  font-size: ${fontSize(24)};
  font-weight: bold;
  ${mediaMax.tablet} {
    font-size: ${fontSize(18)};
  }
`;

const ActionContainer = styled.div`
  margin: 0 auto ${spacing(24)};
  display: flex;
  justify-content: space-between;
  ${mediaMax.tablet} {
    margin: 0 auto ${spacing(16)};
    align-items: center;
    /* display: block;
    width: 100%; */
  }
  text-align: right;
`;

const TitleContainer = styled.div`
  display: flex;
  align-items: center;
  ${mediaMax.tablet} {
    /* margin-bottom: ${spacing(24)}; */
  }
`;
const Desc = styled.div`
  margin-bottom: ${spacing(24)};
`;

const ProfileContainer = styled.div`
  display: flex;
  flex-direction: column;
  border: 1px solid ${gray04};
  border-radius: 16px;
  margin-bottom: ${spacing(16)};
  width: 100%;
  overflow: hidden;
`;
const ProfileTitle = styled.div<{
  isFirstItem?: boolean;
}>`
  font-size: ${fontSize(16)};
  font-weight: bold;
  padding: 0 ${spacing(24)};
  margin-bottom: ${spacing(8)};

  ${({ isFirstItem }) =>
    isFirstItem &&
    css`
      margin-top: ${spacing(24)};
    `}
`;
const ProfileValue = styled.div`
  font-size: ${fontSize(14)};
  padding: 0 ${spacing(24)};
  margin-bottom: ${spacing(24)};
  white-space: pre-wrap;
`;

const StartButton = styled.div`
  padding: 0 ${spacing(24)};
  margin-bottom: ${spacing(24)};
`;
const StartButtonInner = styled(Button)`
  width: 160px;
`;
